const formatMoney = (cents, currency, locale) => {
  try {
    return new Intl.NumberFormat(locale || undefined, {
      style: 'currency',
      currency
    }).format(cents / 100);
  } catch (e) {
    return `${(cents / 100).toFixed(2)} ${currency || ''}`.trim();
  }
};

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

let latestCart = null;
let refreshTimer = null;

function normalizeCartForZig(cart) {
  const bridge = window.ZIG_CART_PRICING_BRIDGE;

  if (
    bridge &&
    typeof bridge.normalizeCart === 'function' &&
    cart &&
    Array.isArray(cart.items)
  ) {
    return bridge.normalizeCart(cart);
  }

  return cart;
}

function normalizeVariantId(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const match = raw.match(/ProductVariant\/(\d+)/);
  if (match && match[1]) return match[1];
  return raw.replace(/\D/g, '');
}

function getBonusConfig() {
  const config = window.BPW_BONUS_CART_HELPER || {};

  return {
    enabled: Boolean(config.enabled),
    threshold: Math.round(Math.max(0, Number(config.minSubtotal || 0)) * 100),
    variantId: normalizeVariantId(config.variantId)
  };
}

function isBonusItem(item, bonusVariantId) {
  if (!item || !bonusVariantId) return false;
  return String(item.variant_id) === String(bonusVariantId);
}

function getCartTotalWithoutBonus(cart, bonusVariantId) {
  return (cart?.items || []).reduce((sum, item) => {
    if (isBonusItem(item, bonusVariantId)) return sum;
    return sum + Number(item.final_line_price || item.line_price || 0);
  }, 0);
}

function isShippingDisabled(bar) {
  return String(bar?.dataset?.shippingDisabled || '').toLowerCase() === 'true';
}

function getThreshold(bar) {
  if (isShippingDisabled(bar)) return 0;
  return Number(bar.dataset.threshold || bar.dataset.shippingThreshold || 0);
}

function getTextEl(bar) {
  return (
    bar.querySelector('[data-free-shipping-text]') ||
    bar.querySelector('[data-free-delivery-text]')
  );
}

function getFillEl(bar) {
  return (
    bar.querySelector('[data-free-shipping-fill]') ||
    bar.querySelector('[data-free-delivery-fill]') ||
    bar.querySelector('.free-delivery-bar__progress-fill')
  );
}

function getBonusMarker(bar) {
  return (
    bar.querySelector('[data-bonus-marker]') ||
    bar.querySelector('[data-free-delivery-bonus-marker]')
  );
}

function getShippingMarker(bar) {
  return (
    bar.querySelector('[data-shipping-marker]') ||
    bar.querySelector('[data-free-delivery-shipping-marker]')
  );
}

function setMarker(marker, position, reached, labelText) {
  if (!marker) return;

  marker.hidden = false;
  marker.style.left = `${clamp(position, 0, 100)}%`;
  marker.classList.toggle('is-reached', Boolean(reached));

  const label = marker.querySelector('.free-delivery-bar__milestone-label');
  if (label && labelText) label.textContent = labelText;
}

function updateBar(bar, cart) {
  if (!bar || !cart || !Array.isArray(cart.items)) return;

  const shippingDisabled = isShippingDisabled(bar);
  const shippingThreshold = getThreshold(bar);

  const currency = bar.dataset.currency || cart?.currency?.iso_code || 'EUR';
  const locale = bar.dataset.locale || document.documentElement.lang || 'it-IT';

  const bonus = getBonusConfig();
  const bonusEnabled = bonus.enabled && bonus.variantId && bonus.threshold > 0;

  if (!shippingThreshold && !bonusEnabled) return;

  const total = bonusEnabled
    ? getCartTotalWithoutBonus(cart, bonus.variantId)
    : Number(cart.total_price || 0);

  const maxThreshold = shippingDisabled && bonusEnabled
    ? bonus.threshold
    : bonusEnabled
      ? Math.max(shippingThreshold, bonus.threshold)
      : shippingThreshold;

  if (!maxThreshold) return;

  const progressPct = clamp((total / maxThreshold) * 100, 0, 100);

  const fillEl = getFillEl(bar);
  const textEl = getTextEl(bar);
  const trackEl = bar.querySelector('.free-delivery-bar__progress');

  if (fillEl) fillEl.style.width = `${progressPct}%`;

  if (trackEl) {
    trackEl.setAttribute('aria-valuenow', String(Math.round(progressPct)));
  }

  const bonusMarker = getBonusMarker(bar);
  const shippingMarker = getShippingMarker(bar);

  if (bonusMarker) {
    if (bonusEnabled) {
      setMarker(
        bonusMarker,
        (bonus.threshold / maxThreshold) * 100,
        total >= bonus.threshold,
        bar.dataset.bonusLabel || 'Regalo'
      );
    } else {
      bonusMarker.hidden = true;
    }
  }

  if (shippingMarker) {
    if (shippingDisabled || !shippingThreshold) {
      shippingMarker.hidden = true;
      shippingMarker.classList.remove('is-reached');
    } else {
      setMarker(
        shippingMarker,
        (shippingThreshold / maxThreshold) * 100,
        total >= shippingThreshold,
        bar.dataset.shippingLabel || 'Spedizione gratuita'
      );
    }
  }

  if (!textEl) {
    return;
  }

  const bonusRemaining = bonusEnabled ? Math.max(0, bonus.threshold - total) : 0;
  const shippingRemaining = shippingDisabled ? 0 : Math.max(0, shippingThreshold - total);

  const giftAmount = formatMoney(bonusRemaining, currency, locale);
  const shippingAmount = formatMoney(shippingRemaining, currency, locale);

  if (shippingDisabled) {
    if (bonusEnabled && bonusRemaining > 0) {
      textEl.textContent =
        (bar.dataset.bonusRemaining || 'Aggiungi [amount] per ricevere un regalo')
          .replace('[amount]', giftAmount);
        return;
    }

    if (bonusEnabled) {
      textEl.textContent = bar.dataset.bonusUnlocked || 'Hai sbloccato il prodotto bonus';
        return;
    }

    bar.hidden = true;
    return;
  }

  if (bonusEnabled && bonusRemaining > 0 && shippingRemaining > 0) {
    textEl.textContent =
      (bar.dataset.bothRemaining || 'Aggiungi [shipping] per la spedizione gratuita e [bonus] per ricevere il regalo')
        .replace('[shipping]', shippingAmount)
        .replace('[bonus]', giftAmount);
    return;
  }

  if (bonusEnabled && bonusRemaining > 0) {
    if (!shippingRemaining) {
      textEl.textContent =
        (bar.dataset.shippingUnlockedBonusRemaining || 'Spedizione gratuita sbloccata. Aggiungi [bonus] per ricevere il regalo')
          .replace('[bonus]', giftAmount);
        return;
    }

    textEl.textContent =
      (bar.dataset.bonusRemaining || 'Aggiungi [amount] per ricevere un regalo')
        .replace('[amount]', giftAmount);
    return;
  }

  if (shippingRemaining > 0) {
    textEl.textContent =
      bonusEnabled
        ? (bar.dataset.bonusAndShipping || 'Regalo sbloccato. Aggiungi [amount] per la spedizione gratuita').replace('[amount]', shippingAmount)
        : (bar.dataset.shippingRemaining || 'Aggiungi [amount] per la spedizione gratuita').replace('[amount]', shippingAmount);
    return;
  }

  textEl.textContent = bonusEnabled
    ? (bar.dataset.allUnlocked || 'Hai sbloccato il regalo e la spedizione gratuita')
    : (bar.dataset.shippingReached || 'Hai sbloccato la spedizione gratuita');
}

async function fetchCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
    cache: 'no-store'
  });

  if (!response.ok) throw new Error('Unable to load cart');

  const cart = await response.json();
  return normalizeCartForZig(cart);
}

function updateAllBars(cart) {
  cart = normalizeCartForZig(cart);

  if (!cart || !Array.isArray(cart.items)) return;

  latestCart = cart;

  document
    .querySelectorAll('[data-free-shipping-bar], [data-free-delivery-bar]')
    .forEach((bar) => updateBar(bar, cart));
}

async function refresh() {
  try {
    const cart = await fetchCart();
    updateAllBars(cart);
  } catch (e) {
    // silent
  }
}

function scheduleRefresh(delay = 80) {
  if (refreshTimer) clearTimeout(refreshTimer);

  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    refresh();
  }, delay);
}

function boot(root = document, cart = null) {
  if (cart && Array.isArray(cart.items)) {
    updateAllBars(cart);
    return;
  }

  if (latestCart && Array.isArray(latestCart.items)) {
    updateAllBars(latestCart);
  }

  scheduleRefresh(80);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot());
} else {
  boot();
}

document.addEventListener('shopify:section:load', (event) => {
  boot(event.target);
});

document.addEventListener('cart:update', (event) => {
  const cart = event.detail?.resource || event.detail?.cart || event.detail;

  if (cart && Array.isArray(cart.items)) {
    updateAllBars(cart);
  }

  scheduleRefresh(120);
});

document.addEventListener('cart:updated', () => scheduleRefresh(120));
document.addEventListener('cart:refresh', () => scheduleRefresh(120));
document.addEventListener('bpw:cart:updated', () => scheduleRefresh(120));

setTimeout(() => scheduleRefresh(0), 300);
setTimeout(() => scheduleRefresh(0), 1000);